import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { getPageSource } from "@MasterBluePrint/Tracking/trackingHelper";
import { getVendorStockStatus, isHazardousProduct } from "@MasterBluePrint/Tracking/AddToCart/Utils/addToCartUtils";

export type AddtoCartTrackingPayload = {
    event: "addToCart";
    ecommerce: {
        currencyCode?: string;
        add: {
            actionField: {
                id?: string;
                list: string;
                action: "add";
            };
            products: Array<{
                name: string;
                id: string;
                price?: number;
                brand?: string;
                condition?: string;
                quantity: number | null;
                stock: string;
                category?: string;
                position?: string;
                vendorStockQty: number | string;
                vendorStockStatus: string | "";
                Hazardous?: "Yes" | "";
            }>;
        };
    };
};

const buildAddToCartTracking = (
    product: CartLineModel,
    productInfo: ProductInfo,
    state: ApplicationState,
    currentUrl: string
): AddtoCartTrackingPayload => {
    let productCondition: string | undefined = "";
    let wishListIdWhenShared: string | undefined;

    if (product.productId) {
        const productData = getProductState(state, product.productId).value;
        productCondition = productData?.properties?.condition || product?.properties?.cartLineCondition;
    }

    const pageSourceResult = getPageSource(currentUrl, product.properties?.productSourceActionField ?? "Product Details");
    if (pageSourceResult === "Shared Cart") {
        const wishList = getWishListState(state, state.pages.staticList.wishListId).value;
        wishListIdWhenShared = wishList?.id;
    }

    const qtyOnHand = product?.qtyOnHand ?? 0;
    // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
    const availabilityMessage = Number(qtyOnHand) > 0
        ? "In Stock"
        : product?.properties?.isDirectShipFromManufacturer?.toLowerCase?.() === "true"
            ? "DirectShip" : "Out Of Stock";
    const hazardousProduct = isHazardousProduct(product);
    const vendorStockInfo = getVendorStockStatus(product, productInfo);
    const currencyCode = state.context.session.currency?.currencyCode ?? "";

    const actionField: { id?: string; list: string; action: "add" } = pageSourceResult === "Shared Cart"
        ? {
            id: wishListIdWhenShared,
            list: pageSourceResult,
            action: "add",
        }
        : {
            list: pageSourceResult,
            action: "add",
        };
    return {
        event: "addToCart",
        ecommerce: {
            currencyCode,
            add: {
                actionField,
                products: [{
                    name: product.shortDescription,
                    id: product.erpNumber,
                    price: product.pricing?.actualPrice,
                    brand: product.properties?.cartLineBrandName,
                    condition: productCondition || "",
                    quantity: product.qtyOrdered,
                    stock: availabilityMessage,
                    category: product.properties?.cartlineCategoryName,
                    position: product?.properties?.productListPosition ?? "",
                    vendorStockStatus: vendorStockInfo.vendorStockStatus,
                    vendorStockQty: vendorStockInfo?.vendorStockQty !== 0 ? vendorStockInfo?.vendorStockQty : "",
                    Hazardous: hazardousProduct,
                }],
            },
        },
    };
}

export default buildAddToCartTracking;